import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Brain, Zap, Wrench, Smartphone, GraduationCap, ShieldCheck } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import CommandCenter from "@/pages/CommandCenter";
import TerminalPage from "@/pages/TerminalPage";
import AgentsPage from "@/pages/AgentsPage";
import PluginsPage from "@/pages/PluginsPage";
import RevenuePage from "@/pages/RevenuePage";
import WorkspacesPage from "@/pages/WorkspacesPage";
import CommandsPage from "@/pages/CommandsPage";
import SettingsPage from "@/pages/SettingsPage";
import FilesPage from "@/pages/FilesPage";
import LockScreen from "@/pages/LockScreen";
import { OwnerPasswordSetup } from "@/components/OwnerPasswordSetup";
import { AuthenticatorEnrollment } from "@/components/AuthenticatorReplacement";
import Placeholder from "@/pages/Placeholder";
import TasksPage from "@/pages/TasksPage";
import ActivityPage from "@/pages/ActivityPage";
import AIUsagePage from "@/pages/AIUsagePage";
import VaultPage from "@/pages/VaultPage";
import ServersPage from "@/pages/ServersPage";
import ModelsPage from "@/pages/ModelsPage";
import "@/index.css";

const MODULES = [
  { path: "automations", title: "Automations", icon: Zap, description: "Event-driven workflows and triggers — health checks, security scans, backups and morning briefings." },
  { path: "devices", title: "Devices", icon: Smartphone, description: "Connected device registry with secure pairing, trust levels, per-device permissions and capability routing (iPhone, Mac, servers)." },
  { path: "skills", title: "Skills", icon: GraduationCap, description: "Reusable skill library — SOPs, templates, scripts and validation rules with versioning and rollback." },
  { path: "tools", title: "Tools", icon: Wrench, description: "Registry of individual callable tools exposed by plugins, explicitly assigned to agents." },
  { path: "memory", title: "Memory", icon: Brain, description: "Long-term vector memory and knowledge graphs scoped per workspace." },
  { path: "security", title: "Security", icon: ShieldCheck, description: "Security Sentinel — baseline monitoring, intrusion & persistence detection, file integrity and investigation." },
];

function Gate() {
  const { unlocked, checking, restoreError, restore, passwordSetupRequired, authenticatorEnrollment } = useAuth();
  if (checking || restoreError) return <div className="fixed inset-0 jv-grid flex items-center justify-center p-6" data-testid="session-restore-gate">
    <div className="jv-panel p-6 max-w-md text-center">
      <p role={restoreError ? "alert" : "status"} data-testid="session-restore-status" className="text-sm text-jarvis-cyan">{restoreError || "Checking your saved session…"}</p>
      {restoreError && <button data-testid="session-restore-retry" onClick={restore} className="min-h-11 mt-4 px-4 border border-jarvis-cyan/40 rounded text-jarvis-cyan">Retry connection</button>}
    </div>
  </div>;
  if (authenticatorEnrollment) return <AuthenticatorEnrollment />;
  if (passwordSetupRequired) return <OwnerPasswordSetup />;
  if (!unlocked) return <LockScreen />;
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<CommandCenter />} />
          <Route path="workspaces" element={<WorkspacesPage />} />
          <Route path="terminal" element={<TerminalPage />} />
          <Route path="agents" element={<AgentsPage />} />
          <Route path="commands" element={<CommandsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="files" element={<FilesPage />} />
          <Route path="plugins" element={<PluginsPage />} />
          <Route path="revenue" element={<RevenuePage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="ai-usage" element={<AIUsagePage />} />
          <Route path="vault" element={<VaultPage />} />
          <Route path="servers" element={<ServersPage />} />
          <Route path="models" element={<ModelsPage />} />
          {MODULES.map((m) => (
            <Route key={m.path} path={m.path} element={<Placeholder title={m.title} icon={m.icon} description={m.description} />} />
          ))}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <Gate />
      </WorkspaceProvider>
    </AuthProvider>
  );
}

export default App;
